import React, { useEffect, useState, useCallback, Fragment } from "react";
import { motion } from "framer-motion";
import { useAdAssistantDimensions } from "../hooks/useAdAssistantDimensions";
import { ModalWrapper, ScrollableContent, FixedColumn } from "./AllVariantsModal.styled";
import { CartNavigationWrapper, FlexWrapper, CartSummary } from "../AdAssistant.styled";
import { FadeInOpacityAbsoluteAnimation } from "../../../animations/animations";
import OrientationsTemplatePreview from "../previews/orientations-template-preview/OrientationsTemplatePreview";
import AdAssistantStore from "../AdAssistantStore";
import { TEMPLATES } from "./../AdAssistant";
import { withRefluxStore } from "../../../Stores";
import AdAssistantHeader from "../ad-assistant-header/AdAssistantHeader";
import Cart from "../cart/Cart";
import SaveNavigation from "../save-navigation/SaveNavigation";
import { ActionsAdAssistant } from "../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import { Trans, useTranslation } from "react-i18next";
import { Store__System } from "@talentbait/shared-components";
import { useInfiniteScroll } from "../../../hooks/use-infinite-scroll.hook";

const calculateInitialItems = () => {
  return window.innerWidth < 1920 ? 6 : 8;
};

const AllVariantsModal = ({
  toggleEditionModal,
  orientationsOptions,
  viewSelected,
  handleViewChange,
  emitSaveCampaign,
  emitUseAd,
  emitDismissAd,
  // Store
  userProfile,
  allAdCombinations,
  usedAds,
  dismissedAds,
  campaignId,
  currentAd,
  jobTitle,
  campaignData
}) => {
  const dimensions = useAdAssistantDimensions();
  const { t } = useTranslation();
  const [initialItems, setInitialItems] = useState(calculateInitialItems());
  const [itemsPerLoad, setItemsPerLoad] = useState(calculateInitialItems());

  const [orientationSelected, setOrientationSelected] = useState(orientationsOptions[0]);
  const [hoveredPreviews, setHoveredPreviews] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingEdition, setPendingEdition] = useState(null);
  const [isUseSelected, setIsUseSelected] = useState(false);
  const [isDismissSelected, setIsDismissSelected] = useState(false);

  const { visibleItems, loadMoreRef, resetItems } = useInfiniteScroll({
    scrollContainerId: "scrollable-content",
    initialItems,
    itemsPerLoad
  });

  useEffect(() => {
    const handleResize = () => {
      const newInitialItems = calculateInitialItems();
      setInitialItems(newInitialItems);
      setItemsPerLoad(newInitialItems);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (pendingAction) {
      const { isUse } = pendingAction;
      isUse ? emitUseAd() : emitDismissAd();
      setPendingAction(null);
    }
  }, [pendingAction, emitUseAd, emitDismissAd]);

  useEffect(() => {
    if (pendingEdition) {
      toggleEditionModal({ type: "text" });
      setPendingEdition(null);
    }
  }, [pendingEdition, toggleEditionModal]);

  const handleOrientationChange = (selectedOrientation) => {
    setOrientationSelected(selectedOrientation);
  };

  const handleAdSelection = (isUse, adId) => {
    const addToSelect = allAdCombinations.find((ad) => ad.id === adId);
    ActionsAdAssistant.setCurrentAd(addToSelect);
    setPendingAction({ isUse });
  };

  const handleAdSelectionEdition = (adId, isAdCreated) => {
    let addToSelect = allAdCombinations.find((ad) => ad.id === adId);

    if (isAdCreated) {
      ActionsAdAssistant.updateAd(
        {
          isLocked: true,
          lockedBy: userProfile.userName,
          lockedUserId: userProfile.userId,
          lockedDate: new Date().toISOString()
        },
        adId
      );

      addToSelect = {
        ...addToSelect,
        isLocked: true,
        lockedBy: userProfile.userName,
        lockedUserId: userProfile.userId,
        lockedDate: new Date().toISOString()
      };
    }

    ActionsAdAssistant.setCurrentAd(addToSelect);
    setPendingEdition({ adId });
  };

  const handlePreviewHover = useCallback((assetIndex, isHovered) => {
    setHoveredPreviews((prev) => ({ ...prev, [assetIndex]: isHovered }));
  }, []);

  const combinationsToShow = allAdCombinations.filter((combination) => {
    const isUsed = usedAds.some((ad) => ad.id === combination.id);
    const isDismissed = dismissedAds.some((ad) => ad.id === combination.id);

    // Default case: show all combinations plus used, but not dismissed
    if (!isUseSelected && !isDismissSelected) {
      return !isDismissed || isUsed;
    }

    // If isUseSelected, show only used ones
    if (isUseSelected && !isDismissSelected) {
      return isUsed;
    }

    // If isDismissSelected, show only dismissed ones
    if (!isUseSelected && isDismissSelected) {
      return isDismissed;
    }

    // If both are true, show only used and dismissed
    if (isUseSelected && isDismissSelected) {
      return isUsed || isDismissed;
    }

    return false;
  });

  useEffect(() => {
    if (isUseSelected || isDismissSelected || initialItems || itemsPerLoad) {
      const scrollableContent = document.getElementById("scrollable-content");
      scrollableContent?.scrollTo({ top: 0, behavior: "smooth" });
      resetItems();
    }
  }, [isUseSelected, isDismissSelected, resetItems, initialItems, itemsPerLoad]);

  // Slice the combinations to show only the visible items
  const visibleCombinations = combinationsToShow.slice(0, visibleItems);

  const adIndexCount = allAdCombinations.findIndex((ad) => ad.id === currentAd.id) + 1;

  const targetCities = campaignData?.locations.map(({ location }) => location).join(", ");

  return (
    <motion.div {...FadeInOpacityAbsoluteAnimation}>
      <AdAssistantHeader
        viewSelected={viewSelected}
        emitViewChange={handleViewChange}
        isAllVariants={true}
        isShowUseSelected={isUseSelected}
        isShowDismissSelected={isDismissSelected}
        setUseSelected={setIsUseSelected}
        setDismissSelected={setIsDismissSelected}
        adIndexCount={adIndexCount}
        totalAds={allAdCombinations.length}
        // orientation
        orientationsOptions={orientationsOptions}
        orientationSelected={orientationSelected}
        handleOrientationChange={handleOrientationChange}
      />

      <ModalWrapper>
        <ScrollableContent id="scrollable-content">
          <FlexWrapper gap={20} align={"flex-start"} wrap={"wrap"} justify="space-around">
            {!allAdCombinations ? (
              <div>{t("No combinations available")}</div>
            ) : (
              visibleCombinations.map((combination, assetIndex) => {
                const orientation = orientationSelected?.value;
                const currentCombinationMediaAsset = combination.mediaAssets[orientation];
                const mediaAssetInputProps = currentCombinationMediaAsset.inputProps;
                const TemplateComponent = TEMPLATES[currentCombinationMediaAsset.templateId];
                const isVideoTemplate = currentCombinationMediaAsset.type === "video";
                const asset =
                  currentCombinationMediaAsset.type === "video"
                    ? mediaAssetInputProps.video
                    : mediaAssetInputProps.backgroundImage;

                const videoDuration =
                  currentCombinationMediaAsset.type === "video" ? mediaAssetInputProps.duration : 0;

                const dimensionsPreview =
                  dimensions[currentCombinationMediaAsset.templateId]?.[
                    orientationSelected?.value
                  ] || dimensions.default;

                const isUsed = usedAds.some((ad) => ad.id === combination.id);
                const isDismissed = dismissedAds.some((ad) => ad.id === combination.id);
                const isAdCreated = isUsed || isDismissed;

                return (
                  <Fragment key={assetIndex}>
                    <OrientationsTemplatePreview
                      isTemplateVideo={isVideoTemplate}
                      dimensionsPreview={dimensionsPreview}
                      templateInputProps={mediaAssetInputProps}
                      autoCroppingImage={asset}
                      orientationSelected={orientationSelected?.value}
                      videoDuration={videoDuration || 0}
                      TemplateComponent={TemplateComponent}
                      toggleAdSelection={(isUse) => handleAdSelection(isUse, combination.id)}
                      toggleAdSelectionEdition={() =>
                        handleAdSelectionEdition(combination.id, isAdCreated)
                      }
                      setIsAdPreviewHovered={(isHovered) =>
                        handlePreviewHover(assetIndex, isHovered)
                      }
                      isAdPreviewHovered={hoveredPreviews[assetIndex]}
                      isUsed={isUsed}
                      isDismissed={isDismissed}
                      editedAd={combination}
                      userProfile={userProfile}
                    />
                  </Fragment>
                );
              })
            )}
            <div ref={loadMoreRef} style={{ height: "10px", width: "100%" }} />
          </FlexWrapper>
        </ScrollableContent>

        <FixedColumn>
          <CartSummary>
            <Trans
              i18nKey="campaign_sentence"
              values={{ jobTitle, cities: targetCities }}
              components={{
                openPosition: <span id="highlight" />,
                jobTitle: <span id="job-title" />,
                cities: <span id="city-name" />
              }}
            />
          </CartSummary>
          <CartNavigationWrapper>
            <Cart selectedItems={usedAds.length} />
            <SaveNavigation
              selectedItems={usedAds.length}
              emitSaveCampaign={(data) => emitSaveCampaign(data)}
              navigationUrl={`/assistant/overview/${campaignId}`}
              navigationText={"Accept selection"}
            />
          </CartNavigationWrapper>
        </FixedColumn>
      </ModalWrapper>
    </motion.div>
  );
};

export default withRefluxStore(
  AllVariantsModal,
  [AdAssistantStore, Store__System],
  [
    "allAdCombinations",
    "usedAds",
    "dismissedAds",
    "userProfile",
    "currentAd",
    "jobTitle",
    "campaignData"
  ]
);
